import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BiBot, BiPlus } from 'react-icons/bi';
import { useAIDispatch, useAISelector } from '../store';
import { fetchAIAssistants } from '../store/slices/aiAssistantSlice';
import { useAIModule } from '../contexts/AIModuleContext';
import AIAgentPanel from '../../../components/AIAgentPanel';

const AIAssistants: React.FC = () => {
  const dispatch = useAIDispatch();
  const { theme } = useAIModule();
  const { assistants, loading } = useAISelector(state => state.aiAssistants);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAIAssistants());
  }, [dispatch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gray-50'}`}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex justify-between items-center"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
            AI Assistants
          </h1>
          <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage and configure your AI assistants
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold"
        >
          <BiPlus size={20} />
          Create AI Assistant
        </motion.button>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assistants.length > 0 ? assistants.map((assistant: any, index: number) => (
          <motion.div
            key={assistant.id || index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05, y: -5 }}
            onClick={() => setSelectedAssistantId(assistant.id || `assistant-${index}`)}
            className={`p-6 rounded-xl cursor-pointer ${
              theme === 'dark' 
                ? 'bg-gray-800/80 border border-gray-700/50' 
                : 'bg-white/90 border border-gray-200/50 shadow-lg'
            }`}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className={`p-3 rounded-lg bg-gradient-to-br ${
                index % 3 === 0 ? 'from-purple-500 to-pink-500' :
                index % 3 === 1 ? 'from-blue-500 to-cyan-500' :
                'from-green-500 to-emerald-500'
              }`}>
                <BiBot size={24} />
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {assistant.name || `AI Assistant ${index + 1}`}
                </h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {assistant.description || 'AI Assistant for customer support'}
                </p>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                assistant.status === 'active' ? 'bg-green-100 text-green-800' :
                assistant.status === 'training' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {assistant.status || 'active'}
              </span>
              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {Math.floor(Math.random() * 100) + 20} conversations
              </div>
            </div>
          </motion.div>
        )) : (
          <div className={`col-span-full text-center py-16 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <BiBot size={64} className="mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold mb-2">No AI Assistants Yet</h3>
            <p>Create your first AI assistant to get started with automated conversations.</p>
          </div>
        )}
      </div>

      {/* AI Agent Panel Modal */}
      {selectedAssistantId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`rounded-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-white'
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                AI Agents Configuration
              </h2>
              <button
                onClick={() => setSelectedAssistantId(null)}
                className={`p-2 rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-800 text-gray-400 hover:text-white' 
                    : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                }`}
              >
                ✕
              </button>
            </div>
            <AIAgentPanel assistantId={selectedAssistantId} />
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AIAssistants;